import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Suspense, lazy } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { AttributionControl } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { formatInTimeZone } from 'date-fns-tz';
import 'react-datepicker/dist/react-datepicker.css';
import { Map as MapIcon, Videocam as VideocamIcon } from '@mui/icons-material';
import Timeline from './components/Timeline';
import LayerSelector from './components/LayerSelector';
import GOESContrailLayer from './components/GOESContrailLayer';
import ADSBGlobalLayer from './components/ADSBGlobalLayer';
import GmnContrailLayer from './components/GmnContrailLayer';
import ContrailVideo from './components/ContrailVideo';
import FieldOfViewLayer from './components/FOVLayer';
import CameraLayer from './components/CameraLayer';
import KeyboardControls from './components/KeyboardControls';
import { ContrailProvider } from './components/ContrailContext';
import debounce from 'lodash/debounce';

const LazyMap = lazy(() => import('react-map-gl').then(module => ({ default: module.Map })));

const ContrailsMap = () => {
  const initializedFromUrl = useRef(false);
  const [initialPosition, setInitialPosition] = useState(null);
  const urlParamsProcessed = useRef(false);
  const [currentDateTime, setCurrentDateTime] = useState(() => new Date());
  const [cachedData, setCachedData] = useState({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [selectedContrail, setSelectedContrail] = useState(null);
  const mapRef = useRef(null);
  const [currentZoom, setCurrentZoom] = useState(3);
  const [viewMode, setViewMode] = useState('map'); // 'map' or 'video'

  // This effect reads URL params and sets initial state
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    
    const lat = params.get('lat');
    const lng = params.get('lng');
    const zoom = params.get('zoom');
    const timestamp = params.get('time');
    
    // Process and validate the coordinates before setting them
    if (lat && lng && zoom) {
      const parsedLat = parseFloat(lat);
      const parsedLng = parseFloat(lng);
      const parsedZoom = parseFloat(zoom);
      
      // Validate values to prevent [0,0] issues
      if (!isNaN(parsedLat) && !isNaN(parsedLng) && !isNaN(parsedZoom) &&
          parsedLat >= -90 && parsedLat <= 90 && 
          parsedLng >= -180 && parsedLng <= 180 &&
          parsedZoom >= 0) {
        // console.log("Setting initial position from URL:", parsedLng, parsedLat, parsedZoom);
        setInitialPosition({
          longitude: parsedLng,
          latitude: parsedLat,
          zoom: parsedZoom
        });
        initializedFromUrl.current = true;
      }
    }
    
    // Set date/time if provided
    if (timestamp) {
      const time = new Date(parseInt(timestamp));
      if (!isNaN(time.getTime())) {
        setCurrentDateTime(time);
      }
    }
    
    urlParamsProcessed.current = true;
  }, []);


  // Update URL when map view changes (debounced)
  const updateUrlParams = useCallback(
    debounce((dateTime) => {
      // Only start updating URL after we've processed the initial URL parameters
      if (!urlParamsProcessed.current) return;
      if (!mapRef.current || !mapRef.current.getMap) return;
      
      // Get the current map center and check if it's at [0,0]
      try {
        const map = mapRef.current.getMap();
        const center = map.getCenter();
        
        // Skip updating if the map has defaulted to [0,0]
        if (Math.abs(center.lat) < 0.001 && Math.abs(center.lng) < 0.001) {
          // console.log("Skipping URL update for [0,0] coordinates");
          return;
        }
        
        const zoom = map.getZoom();
        
        const params = new URLSearchParams(window.location.search);
        params.set('lat', center.lat.toFixed(6));
        params.set('lng', center.lng.toFixed(6));
        params.set('zoom', zoom.toFixed(2));
        params.set('time', dateTime.getTime().toString());
        
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, '', newUrl);
        // console.log("Updated URL with new position:", center.lng, center.lat, zoom);
      } catch (error) {
        console.error("Error updating URL:", error);
      }
    }, 500),
    [] // Empty dependency array since we pass currentDateTime as an argument
  );

  // This effect handles map initialization after it loads
  useEffect(() => {
    if (mapRef.current && mapRef.current.getMap && initialPosition) {
      try {
        const map = mapRef.current.getMap();
        map.jumpTo(initialPosition);
      } catch (error) {
        console.error("Error initializing map position:", error);
      }
    }
  }, [initialPosition]);

  // Add to your existing handleMapMove function
  const handleMapMove = useCallback((evt) => {
    if (evt.viewState) {
      setCurrentZoom(evt.viewState.zoom);
      updateUrlParams(currentDateTime); // Update URL params when map moves
    }
  }, [updateUrlParams, currentDateTime]);

  // Update URL when time changes
  useEffect(() => {
    updateUrlParams(currentDateTime);
  }, [currentDateTime, updateUrlParams]);

  const toggleViewMode = () => {
    setViewMode(viewMode === 'map' ? 'video' : 'map');
  };

  const handleDateTimeChange = useCallback((newDateTime) => {
    setCurrentDateTime(newDateTime);
  }, []);

  const handlePlayStateChange = useCallback((playState) => {
    setIsPlaying(playState);
  }, []);

  const handleMapClick = useCallback((event) => {
    if (!mapRef.current) {
      console.log("Map reference not available");
      return;
    }
  
    const map = mapRef.current.getMap();
    const bbox = [
      [event.point.x - 5, event.point.y - 5],
      [event.point.x + 5, event.point.y + 5]
    ];
  
    // Check if the layer exists before querying
    let globalAdsbFeatures = [];
    if (map.getLayer('adsb-global-adsb-global-position-layer')) {
      globalAdsbFeatures = map.queryRenderedFeatures(bbox, {
        layers: ['adsb-global-adsb-global-position-layer']
      });
    }
    
    // Check if the layer exists before querying
    let contrailFeatures = [];
    if (map.getLayer('gmn-contrails-gmn-contrail-fill')) {
      contrailFeatures = map.queryRenderedFeatures(bbox, {
        layers: ['gmn-contrails-gmn-contrail-fill']
      });
    }
  
    if (globalAdsbFeatures.length > 0) {
      setSelectedFlight(globalAdsbFeatures[0].properties);
      setSelectedContrail(null);
      return;
    }
    
    if (contrailFeatures.length > 0) {
      setSelectedContrail(contrailFeatures[0]);
      setSelectedFlight(null);
      return;
    }
  
    setSelectedFlight(null);
    setSelectedContrail(null);
    return;
  }, []);

  const handleFlightClose = useCallback(() => {
    setSelectedFlight(null);
  }, []);

  const [layers, setLayers] = useState([
    { id: 'goes-contrails', name: 'GOES Contrails', visible: true },
    { id: 'adsb-global', name: 'ADS-B Data (Global)', visible: false },
    { id: 'gmn-contrails', name: 'GMN Data', visible: true },
    { id: 'field-of-views', name: 'Field of Views', visible: false },
    { id: 'cameras', name: 'Camera Stations', visible: true },
  ]);

  // Utility function for formatting dates in UTC
  const formatDateUTC = (date) => {
    return formatInTimeZone(date, 'UTC', 'yyyy-MM-dd');
  };

  // Function to toggle layer visibility
  const toggleLayer = (layerId) => {
    setLayers(prevLayers =>
      prevLayers.map(layer =>
        layer.id === layerId ? { ...layer, visible: !layer.visible } : layer
      )
    );
  };

  const addCachedHour = useCallback((layerId, date, hour) => {
    const dateStr = formatDateUTC(date);
    setCachedData(prev => ({
      ...prev,
      [layerId]: {
        ...prev[layerId],
        [dateStr]: [...new Set([...(prev[layerId]?.[dateStr] || []), hour])]
      }
    }));
  }, []);

  const formatDateTimeWithUnix = (date) => {
    const isoString = date.toISOString().slice(0, 19).replace('T', ' ') + ' UTC';
    const unixTime = Math.floor(date.getTime() / 1000);
    const formattedUnixTime = unixTime.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    
    // Calculate the day of the year (DOY)
    const startOfYearUTC = Date.UTC(date.getUTCFullYear(), 0, 1);
    const diffMs = date.getTime() - startOfYearUTC;
    const doy = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;
  
    return {
      isoString,
      unixTime,
      formattedUnixTime,
      doy
    };
  };

  return (
    <Box sx={{ width: '100%', height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Keyboard Controls */}
      <KeyboardControls
        onTimeChange={handleDateTimeChange}
        currentDateTime={currentDateTime}
        isPlaying={isPlaying}
        onPlayStateChange={handlePlayStateChange}
      />
      
      {/* Common Timeline Control */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        gap: 2, 
        width: '100%', 
        padding: '8px', 
        backgroundColor: 'rgba(225, 225, 225)' 
      }}>
        <Timeline
          onDateTimeChange={handleDateTimeChange}
          cachedData={cachedData}
          onPlayStateChange={handlePlayStateChange}
          currentDateTime={currentDateTime}
        />
      </Box>

      {/* Toggle Button */}
      <Button
        variant="contained"
        color="primary"
        startIcon={viewMode === 'map' ? <VideocamIcon /> : <MapIcon />}
        onClick={toggleViewMode}
        sx={{
          position: 'absolute',
          top: '70px',
          right: '20px',
          zIndex: 1000,
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
        }}
      >
        {viewMode === 'map' ? 'View Videos' : 'View Map'}
      </Button>

      {/* Map View */}
      {viewMode === 'map' && (
        <Box sx={{ position: 'relative', width: '100%', flex: 1 }}>
          <Suspense fallback={<div>Loading map...</div>}>
            <LazyMap
              ref={mapRef}
              initialViewState={initialPosition || { longitude: -98.5795, latitude: 39.8283, zoom: 3 }}
              style={{ width: '100%', height: '100%' }}
              mapStyle="mapbox://styles/cybis320/cm1xw8inh00b101rd5k2y3mus"
              // mapStyle="mapbox://styles/cybis320/cm9hqm707001y01qnbff44s38"
              mapboxAccessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}
              attributionControl={false}
              onClick={handleMapClick}
              onMove={handleMapMove}
              interactiveLayerIds={
                layers.filter(l => l.visible).flatMap(l => {
                  if (l.id === 'adsb-global') return ['adsb-global-adsb-global-position-layer'];
                  if (l.id === 'gmn-contrails') return ['gmn-contrails-gmn-contrail-fill'];
                  return [];
                })
              }
            >
              <AttributionControl
                compact={true}
                customAttribution={[
                  "<a href='https://www.contrailcast.com' target='_blank' rel='noopener noreferrer'>&copy; ContrailCast</a>",
                  "<a href='https://contrails.webapps.google.com/' target='_blank' rel='noopener noreferrer'>&copy; Google</a>"
                ]}
                position="bottom-right"
              />
              <ContrailProvider>
                {layers.map((layer) => {
                  if (layer.id === 'goes-contrails') {
                    return (
                      <GOESContrailLayer
                        key={layer.id}
                        layerId={layer.id}
                        visible={layer.visible}
                        currentDateTime={currentDateTime}
                        addCachedHour={(layerId, date, hour) => addCachedHour(layer.id, date, hour)}
                        cachedHours={cachedData['goes-contrails'] || {}}
                        isPlaying={isPlaying}
                      />
                    );
                  } else if (layer.id === 'field-of-views') {
                    return (
                      <FieldOfViewLayer
                        key={layer.id}
                        layerId="field-of-views"
                        visible={layer.visible}  
                        currentDateTime={currentDateTime}
                        isPlaying={isPlaying}
                      />
                    );
                  } else if (layer.id === 'adsb-global') {
                    return (
                      <ADSBGlobalLayer
                        key={layer.id}
                        layerId={layer.id}
                        visible={layer.visible}
                        currentDateTime={currentDateTime}
                        addCachedHour={(layerId, date, hour) => addCachedHour(layer.id, date, hour)}
                        cachedHours={cachedData['adsb-global'] || {}}
                        isPlaying={isPlaying}
                        minAltitude={25000}
                        selectedFlight={selectedFlight}
                        onFlightClose={handleFlightClose}
                        currentZoom={currentZoom}
                      />
                    );
                  } else if (layer.id === 'gmn-contrails') {
                      return (
                        <GmnContrailLayer
                          key={layer.id}
                          layerId="gmn-contrails"
                          visible={layer.visible}
                          currentDateTime={currentDateTime}
                          addCachedHour={addCachedHour}
                          cachedHours={cachedData['gmn-contrails'] || {}}
                          isPlaying={isPlaying}
                          selectedContrail={selectedContrail}
                        />
                      );
                  } else if (layer.id === 'cameras') {
                    return (
                      <CameraLayer
                        key={layer.id}
                        layerId="cameras"
                        visible={layer.visible}
                        currentDateTime={currentDateTime}
                      />
                    );
                  }
                  return null;
                })}
              </ContrailProvider>
            </LazyMap>
          </Suspense>

          <Typography
            sx={{
              position: 'absolute',
              top: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'grey',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              padding: '8px 16px',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '1.2rem',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
            }}
          >
            {formatDateTimeWithUnix(currentDateTime).isoString}{" - "}
            {formatDateTimeWithUnix(currentDateTime).doy}{" - "}
            {formatDateTimeWithUnix(currentDateTime).formattedUnixTime}
          </Typography>
          
          <LayerSelector layers={layers} toggleLayer={toggleLayer} />
        </Box>
      )}

      {/* Video View */}
      {viewMode === 'video' && (
        <Box sx={{ position: 'relative', width: '100%', flex: 1 }}>
          <Suspense fallback={<div>Loading videos...</div>}>
            <ContrailVideo
              key="contrail-videos"
              layerId="contrail-videos"
              currentDateTime={currentDateTime}
              addCachedHour={addCachedHour}
              cachedHours={cachedData['contrail-videos'] || {}}
              isPlaying={isPlaying}
              visible={true}
            />
          </Suspense>

          <Typography
            sx={{
              position: 'absolute',
              top: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'grey',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              padding: '8px 16px',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '1.2rem',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
            }}
          >
            {formatDateTimeWithUnix(currentDateTime).isoString}{" - "}
            {formatDateTimeWithUnix(currentDateTime).doy}{" - "}
            {formatDateTimeWithUnix(currentDateTime).formattedUnixTime}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ContrailsMap;